// Галерея ассетов КНБК — иконки элементов колонны, извлечённые из
// design_ref/source/КНБК №1.xlsx (xl/media/*). Файлы лежат в public/knbk/,
// манифест с размерами — в src/knbk_assets.ts (регенерируется скриптом
// extract_knbk_assets.sh).
//
// Это «сырая» галерея: показывает все 113 PNG/JPEG в натуральную величину
// (зум-режим) или вписанные в карточку (fit). Используется как референс при
// отрисовке элементов на колонне в схеме скважины. Лейблы у изображений нет —
// в исходнике это excel-shapes без явных подписей. Имена сопоставит человек.

import { createMemo, createSignal, For } from 'solid-js'
import type { Component } from 'solid-js'
import { KNBK_ASSETS } from './knbk_assets'
import type { KnbkAsset } from './knbk_assets'

type Mode = 'fit' | 'natural'
type SortKey = 'name' | 'aspect' | 'height' | 'width'

function assetUrl(a: KnbkAsset): string {
  return `${import.meta.env.BASE_URL}knbk/${a.name}`
}

// «Высокая узкая» картинка — кандидат на элемент колонны (трубы, УБТ,
// стабилизаторы). Используется для быстрого фильтра.
function isColumnLike(a: KnbkAsset): boolean {
  return a.h / a.w >= 2 && a.h >= 150
}

export const KnbkAssetsPage: Component = () => {
  const [mode, setMode] = createSignal<Mode>('fit')
  const [sort, setSort] = createSignal<SortKey>('name')
  const [columnOnly, setColumnOnly] = createSignal(false)

  const visible = createMemo<KnbkAsset[]>(() => {
    let xs = KNBK_ASSETS.slice()
    if (columnOnly()) xs = xs.filter(isColumnLike)
    switch (sort()) {
      case 'name':
        xs.sort((a, b) => {
          const an = parseInt(a.name.replace(/\D+/g, ''), 10) || 0
          const bn = parseInt(b.name.replace(/\D+/g, ''), 10) || 0
          return an - bn
        })
        break
      case 'aspect':
        xs.sort((a, b) => b.h / b.w - a.h / a.w)
        break
      case 'height':
        xs.sort((a, b) => b.h - a.h)
        break
      case 'width':
        xs.sort((a, b) => b.w - a.w)
        break
    }
    return xs
  })

  return (
    <div class="page page-wide knbk-assets">
      <div class="page-header">
        <div>
          <h1 class="page-title">Ассеты КНБК</h1>
          <p class="page-subtitle">
            Источник: <code>design_ref/source/КНБК №1.xlsx</code> &middot; всего {KNBK_ASSETS.length} картинок.
            Сырой дамп: иконки элементов колонны (трубы, УБТ, долота, стабилизаторы), плюс служебные (логотип, скрины).
          </p>
        </div>

        <div class="knbk-toolbar">
          <div class="knbk-seg">
            <button class={'knbk-seg-btn' + (mode() === 'fit' ? ' active' : '')} onClick={() => setMode('fit')}>
              Вписать
            </button>
            <button class={'knbk-seg-btn' + (mode() === 'natural' ? ' active' : '')} onClick={() => setMode('natural')}>
              1:1
            </button>
          </div>

          <select
            class="knbk-select"
            value={sort()}
            onChange={(e) => setSort(e.currentTarget.value as SortKey)}
          >
            <option value="name">По имени</option>
            <option value="aspect">По вытянутости (h/w)</option>
            <option value="height">По высоте</option>
            <option value="width">По ширине</option>
          </select>

          <label class="knbk-check">
            <input
              type="checkbox"
              checked={columnOnly()}
              onChange={(e) => setColumnOnly(e.currentTarget.checked)}
            />
            <span>Только колонные (h/w &ge; 2)</span>
          </label>
        </div>
      </div>

      <div class={'asset-grid mode-' + mode()}>
        <For each={visible()}>
          {(a) => (
            <figure class="asset-card" title={`${a.name} ${a.w}×${a.h}, ${(a.bytes / 1024).toFixed(1)} KB`}>
              <div class="asset-canvas">
                <img
                  src={assetUrl(a)}
                  alt={a.name}
                  style={
                    mode() === 'natural'
                      ? { width: `${a.w}px`, height: `${a.h}px` }
                      : { 'max-width': '100%', 'max-height': '100%' }
                  }
                  loading="lazy"
                />
              </div>
              <figcaption>
                <span class="asset-name">{a.name}</span>
                <span class="asset-dims">{a.w}×{a.h}</span>
              </figcaption>
            </figure>
          )}
        </For>
      </div>
    </div>
  )
}
